import React, { useMemo, useState } from "react";
import { PageHeader, Result, FormulaBox, Note } from "@/components/CalcUI";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/i18n/LanguageProvider";
import { NIHSS_ITEMS } from "@/lib/score_data";

function interpret(score, t) {
  if (score === null) return { label: "—", tone: "default" };
  if (score === 0) return { label: t("nihss.severity.none"), tone: "success" };
  if (score <= 4) return { label: t("nihss.severity.minor"), tone: "success" };
  if (score <= 15) return { label: t("nihss.severity.moderate"), tone: "warning" };
  if (score <= 20) return { label: t("nihss.severity.moderate_severe"), tone: "warning" };
  return { label: t("nihss.severity.severe"), tone: "danger" };
}

export default function NIHSS() {
  const { t, lang } = useLanguage();
  const [values, setValues] = useState({});

  const score = useMemo(
    () => NIHSS_ITEMS.reduce((sum, it) => sum + (values[it.id] ?? 0), 0),
    [values]
  );

  const answered = Object.keys(values).length;
  const interp = answered === 0 ? interpret(null, t) : interpret(score, t);

  return (
    <div data-testid="page-nihss">
      <PageHeader title={t("nihss.title")} subtitle={t("nihss.subtitle")} formula={t("nihss.formula")} />

      <div className="px-6 md:px-10 py-8 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8 max-w-6xl">
        <div className="space-y-6">
          {NIHSS_ITEMS.map((item) => (
            <div
              key={item.id}
              className="border border-stone-200 bg-white rounded-sm p-5"
              data-testid={`nihss-item-${item.id}`}
            >
              <div className="flex items-baseline justify-between gap-4 mb-3">
                <div className="text-[11px] tracking-[0.08em] uppercase font-semibold text-stone-500">
                  {item.label[lang] || item.label.en}
                </div>
                <div className="font-mono text-xs text-stone-500">
                  {values[item.id] ?? "—"} / {item.options[item.options.length - 1].v}
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {item.options.map((opt) => {
                  const active = values[item.id] === opt.v;
                  const text = opt[lang] || opt.en;
                  return (
                    <button
                      key={opt.v}
                      type="button"
                      data-testid={`nihss-${item.id}-opt-${opt.v}`}
                      onClick={() =>
                        setValues((prev) => ({ ...prev, [item.id]: opt.v }))
                      }
                      className={cn(
                        "text-left text-sm px-3 py-2 rounded-sm border transition-colors duration-100 flex items-start gap-2",
                        active
                          ? "border-sky-700 bg-sky-700/5 text-stone-900"
                          : "border-stone-200 bg-white text-stone-700 hover:bg-stone-50 hover:border-stone-300"
                      )}
                    >
                      <span
                        className={cn(
                          "font-mono text-xs shrink-0 w-6 h-6 inline-flex items-center justify-center rounded-sm border",
                          active
                            ? "border-sky-700 bg-sky-700 text-white"
                            : "border-stone-200 text-stone-500"
                        )}
                      >
                        {opt.v}
                      </span>
                      <span className="leading-snug">{text}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="space-y-4 lg:sticky lg:top-6 lg:self-start">
          <Result
            label={t("nihss.result.total")}
            value={score}
            unit={t("nihss.result.unit", { answered, total: NIHSS_ITEMS.length })}
            big
            testid="result-nihss"
            accent={interp.tone === "danger" ? "danger" : interp.tone === "success" ? "success" : "default"}
          />
          <Result
            label={t("nihss.result.severity")}
            value={interp.label}
            testid="result-severity"
            accent={interp.tone === "danger" ? "danger" : interp.tone === "success" ? "success" : "default"}
          />
          <FormulaBox testid="nihss-breakdown">{t("nihss.legend")}</FormulaBox>
          <Note kind="warning">{t("nihss.note")}</Note>
          <button
            type="button"
            data-testid="nihss-reset"
            onClick={() => setValues({})}
            className="w-full text-xs uppercase tracking-[0.08em] font-semibold border border-stone-200 bg-white hover:bg-stone-50 hover:border-stone-300 transition-colors duration-100 py-2.5 rounded-sm"
          >
            {t("common.reset_all")}
          </button>
        </div>
      </div>
    </div>
  );
}
